import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, BookOpen, Check, RefreshCw, TriangleAlert } from "lucide-react";
import { SearchSelect } from "../../components/SearchSelect";
import { PrimaryButton } from "../../components/ui";
import { ConfirmDialog } from "../../components/crm/ui";
import { Field, TextInput } from "../../components/crm/leadsKit";
import { PageLoader } from "../../components/PageLoader";
import { AccessNotice, fetchApi, reasonOf } from "../../components/marketing/access";

/**
 * Settings → Integrations → Meta Conversions API (no design screen; built like
 * IntegrationDetail). Same calls and rules as the old
 * pages/settings/Integrations/MetaConversionsSetup.js:
 *   GET    /api/integrations/meta-conversions          -> { data: { id, pixel_id, dataset_id, attribution_window_days, is_active } | null }
 *   POST   /api/integrations/meta-conversions          { access_token, pixel_id, dataset_id, attribution_window_days } (save + activate)
 *   POST   /api/integrations/meta-conversions/test     { access_token, pixel_id, dataset_id } -> { data: { pixel_id, pixel_name } }
 *                                                       400 with inconclusive: true = the token can't READ the dataset, which a CAPI token never can
 *   DELETE /api/integrations/meta-conversions
 *   GET    /api/integrations/meta-conversions/events?limit=25 -> { data: { counts, total, skip_reasons, events } }
 *   POST   /api/integrations/meta-conversions/test-event { test_event_code } -> { message, data: { dataset_id, matched_on } }
 *   GET    /api/integrations/meta-conversions/stats    -> { data: { clicks, leads_by_status, recent_clicks, top_campaigns } }
 * The token is never sent back by the backend, so the field is always empty and
 * only required for a first save (the backend still needs it on every save).
 */

const EMPTY = { access_token: "", pixel_id: "", dataset_id: "", attribution_window_days: 7 };
const WINDOWS = [{ value: "1", label: "1 day" }, { value: "7", label: "7 days (recommended)" }, { value: "14", label: "14 days" }, { value: "28", label: "28 days" }];
const EVENT_TONE = { sent: "bg-success-soft text-success", failed: "bg-danger-soft text-danger" };
const STEPS = [
  { title: "Get a Meta access token", desc: "Meta Business Settings → System users → create or select a user → Generate new token." },
  { title: "Find the Pixel ID", desc: "Events Manager → select your pixel → Settings → Pixel ID." },
  { title: "Save", desc: "Save the credentials. The connection test only reads dataset details, which a Conversions API token usually isn't allowed to do." },
  { title: "Verify for real", desc: "Use \"Send a test event\" with a code from Events Manager → Test Events. That exercises what actually matters." },
];
const num = (v) => Number(v || 0).toLocaleString("en-IN");
const ghostBtn = "flex h-10 items-center justify-center gap-2 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist disabled:cursor-not-allowed disabled:opacity-60";

function Notice({ tone, title, children }) {
  const cls = tone === "success" ? "bg-success-soft text-success" : tone === "warning" ? "bg-warning-soft text-warning" : "bg-danger-soft text-danger";
  return <div role={tone === "danger" ? "alert" : "status"} className={`rounded-md p-4 text-small ${cls}`}>{title && <p className="mb-1 font-semibold">{title}</p>}{children}</div>;
}

export default function MetaConversionsSetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, inconclusive, message, data }
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [stats, setStats] = useState(null); // data | { error } | null (none yet)
  const [events, setEvents] = useState(null); // data | { error } | null (loading)
  const [testCode, setTestCode] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [testEvent, setTestEvent] = useState(null); // { ok, message, data }

  const loadIntegration = useCallback(async () => {
    setLoading(true);
    const r = await fetchApi("/api/integrations/meta-conversions");
    if (!r.ok) setLoadError(reasonOf(r));
    else {
      setLoadError(null);
      const d = r.data?.data;
      setExisting(d || null);
      setForm(d ? { access_token: "", pixel_id: d.pixel_id || "", dataset_id: d.dataset_id || "", attribution_window_days: d.attribution_window_days || 7 } : EMPTY);
    }
    setLoading(false);
  }, []);
  const loadStats = useCallback(async () => {
    const r = await fetchApi("/api/integrations/meta-conversions/stats");
    setStats(r.ok ? r.data?.data || null : { error: reasonOf(r) });
  }, []);
  const loadEvents = useCallback(async () => {
    setEvents(null);
    const r = await fetchApi("/api/integrations/meta-conversions/events", { params: { limit: 25 } });
    setEvents(r.ok ? r.data?.data || { counts: {}, total: 0, skip_reasons: [], events: [] } : { error: reasonOf(r) });
  }, []);

  useEffect(() => { loadIntegration(); loadStats(); loadEvents(); }, [loadIntegration, loadStats, loadEvents]);

  const change = (name, value) => { setForm((f) => ({ ...f, [name]: value })); setError(null); setSuccess(null); setTestResult(null); };

  const testConnection = async () => {
    setTesting(true); setTestResult(null); setError(null);
    const r = await fetchApi("/api/integrations/meta-conversions/test", { method: "post", body: { access_token: form.access_token, pixel_id: form.pixel_id, dataset_id: form.dataset_id } });
    setTestResult(r.ok
      ? { success: true, message: "Connection successful!", data: r.data?.data }
      : { success: false, inconclusive: !!r.data?.inconclusive, message: r.message || "Connection test failed" });
    setTesting(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    if (!form.access_token || !form.pixel_id) { setError("Access token and Pixel ID are required"); return; }
    setSaving(true);
    const r = await fetchApi("/api/integrations/meta-conversions", { method: "post", body: form });
    if (r.ok) {
      setSuccess(r.data?.message);
      setExisting({ ...form, access_token: undefined, id: r.data?.data?.id, is_active: true });
      setForm((f) => ({ ...f, access_token: "" }));
      loadStats();
    } else setError(r.message || "Failed to save integration");
    setSaving(false);
  };

  const remove = async () => {
    setConfirmDelete(false);
    const r = await fetchApi("/api/integrations/meta-conversions", { method: "delete" });
    if (!r.ok) { setError(r.message || "Failed to delete integration"); return; }
    setSuccess("Integration deleted successfully");
    setExisting(null); setForm(EMPTY); setStats(null); setTestResult(null);
  };

  const sendTestEvent = async () => {
    setSendingTest(true); setTestEvent(null);
    const r = await fetchApi("/api/integrations/meta-conversions/test-event", { method: "post", body: { test_event_code: testCode.trim() || undefined } });
    setTestEvent(r.ok ? { ok: true, message: r.data?.message, data: r.data?.data } : { ok: false, message: r.message || "Could not reach Meta" });
    setSendingTest(false);
  };

  const back = <Link to="/settings/integrations" className="flex w-fit items-center gap-2 text-small font-semibold text-sky-deep"><ArrowLeft size={16} />Back to integrations</Link>;
  if (loading) return <PageLoader label="Loading Meta Conversions API…" />;
  if (loadError) return <main className="mx-auto max-w-5xl px-6 py-10 md:px-8">{back}<AccessNotice reason={loadError} onRetry={loadIntegration} className="mt-10" /></main>;

  const clicks = stats && !stats.error ? stats.clicks || {} : null;

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-8 sm:px-6 md:px-10">
      {back}
      <header className="flex flex-wrap items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm bg-paper text-body font-semibold shadow-soft">CA</span>
        <div className="min-w-0 flex-1">
          <h1 className="text-h2 font-semibold">Meta Conversions API</h1>
          <p className="mt-1 text-small text-muted">Track WhatsApp ad performance and attribution</p>
        </div>
        {existing && <span className="rounded-full bg-success-soft px-3 py-1.5 text-caption font-semibold text-success">Active</span>}
      </header>

      <div className="grid gap-5 lg:grid-cols-5">
        <div className="flex min-w-0 flex-col gap-5 lg:col-span-3">
          <section className="rounded-lg bg-sky-soft p-5">
            <h2 className="text-body font-semibold">What is this?</h2>
            <p className="mt-2 text-small text-ink/80">
              Meta Conversions API integration lets you track which WhatsApp messages came from your Facebook and Instagram ads. When someone clicks your click-to-WhatsApp ad and messages you, Olli automatically:
            </p>
            <ul className="mt-3 flex flex-col gap-2">
              {["Matches the phone number with Meta's ad click data", "Creates a lead with full campaign attribution (UTM, ad ID, campaign name)", "Tracks ROI and conversion rates for each campaign"].map((item) => (
                <li key={item} className="flex items-start gap-2 text-small"><Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-sky-deep" />{item}</li>
              ))}
            </ul>
          </section>

          <form onSubmit={save} noValidate className="flex flex-col gap-5 rounded-lg bg-paper p-5 shadow-soft">
            <h2 className="text-body font-semibold">{existing ? "Update integration" : "Set up integration"}</h2>
            <Field id="access_token" label="Meta access token *" hint={<>Get this from Meta Business Settings → System users → Generate new token. Required permissions: <code className="rounded-xs bg-mist px-1 font-mono">ads_read</code>, <code className="rounded-xs bg-mist px-1 font-mono">business_management</code></>}>
              <TextInput id="access_token" type="password" autoComplete="off" value={form.access_token} onChange={(e) => change("access_token", e.target.value)} placeholder={existing ? "Enter new token to update" : "Enter your Meta API access token"} />
            </Field>
            <Field id="pixel_id" label="Dataset ID (Pixel ID) *" hint="Events Manager → Data sources. Meta renamed Pixels to Datasets; the ID is the same.">
              <TextInput id="pixel_id" inputMode="numeric" value={form.pixel_id} onChange={(e) => change("pixel_id", e.target.value)} placeholder="123456789012345" />
            </Field>
            <Field id="dataset_id" label="CRM dataset ID (recommended)" hint="Lead status events are sent here when set, falling back to the dataset above. Meta asks for a separate CRM dataset so lifecycle events don't mix with website events.">
              <TextInput id="dataset_id" inputMode="numeric" value={form.dataset_id} onChange={(e) => change("dataset_id", e.target.value)} placeholder="123456789012345" />
            </Field>

            {/* Most of this setup happens in Meta's tools, and a missed step leaves everything
                looking connected while doing nothing, so point at the full guide. */}
            <div className="rounded-md bg-sun-soft p-4">
              <h3 className="text-small font-semibold">Not sure where to get these?</h3>
              <p className="mt-1 text-caption text-ink/80">
                The Dataset ID and token come from Meta Events Manager, and three setup steps happen there before this connection does anything. Miss one and Meta still accepts the events, it just can't optimise your campaigns with them.
              </p>
              <button type="button" onClick={() => navigate("/settings/integrations/meta-conversions/guide")} className="mt-3 flex h-9 items-center gap-2 rounded-full bg-paper px-4 text-caption font-semibold ring-1 ring-line hover:bg-mist">
                <BookOpen size={14} strokeWidth={1.75} />Open the step-by-step guide
              </button>
              <p className="mt-3 text-caption text-muted">
                Which statuses send events is set per stage in <Link to="/settings/lead-statuses" className="font-semibold text-sky-deep underline">Settings → Lead statuses</Link>.
              </p>
            </div>

            <Field id="attribution_window_days" label={<>WhatsApp attribution window <span className="ml-1 rounded-full bg-mist px-2 py-0.5 text-caption font-medium text-muted">Click-to-WhatsApp only</span></>} hint="When someone messages you on WhatsApp, how far back to look for a click-to-WhatsApp ad click to credit. Unrelated to the lead status events above; leave at 7 days if you don't run click-to-WhatsApp ads.">
              <SearchSelect id="attribution_window_days" value={String(form.attribution_window_days)} options={WINDOWS} onChange={(v) => change("attribution_window_days", v)} />
            </Field>

            {error && <Notice tone="danger">{error}</Notice>}
            {success && <Notice tone="success">{success}</Notice>}
            {testResult && (
              <Notice tone={testResult.success ? "success" : testResult.inconclusive ? "warning" : "danger"} title={testResult.inconclusive ? "Couldn't check, and that's expected" : null}>
                {testResult.message}
                {testResult.data && <span className="mt-2 block text-caption"><span className="font-semibold">Pixel name:</span> {testResult.data.pixel_name}<br /><span className="font-semibold">Pixel ID:</span> {testResult.data.pixel_id}</span>}
              </Notice>
            )}

            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={testConnection} disabled={testing || !form.access_token || !form.pixel_id} className={`${ghostBtn} h-12 px-5`}>{testing ? "Testing…" : "Test connection"}</button>
              <PrimaryButton type="submit" loading={saving} loadingLabel="Saving…">{existing ? "Update" : "Save and activate"}</PrimaryButton>
            </div>

            {existing && (
              <div className="border-t border-line pt-5">
                <h3 className="text-small font-semibold text-danger">Danger zone</h3>
                <p className="mt-1 text-caption text-muted">Delete this integration to stop ad tracking.</p>
                <button type="button" onClick={() => setConfirmDelete(true)} className="mt-3 h-10 rounded-full bg-danger px-4 text-small font-semibold text-paper hover:brightness-95">Delete integration</button>
              </div>
            )}
          </form>
        </div>

        <div className="flex min-w-0 flex-col gap-5 lg:col-span-2">
          {/* Lead status events: the only place that shows whether events are actually leaving, and why not. */}
          <section className="rounded-lg bg-paper p-5 shadow-soft">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-body font-semibold">Lead status events</h2>
              <button type="button" onClick={loadEvents} className="flex items-center gap-1.5 text-caption font-semibold text-sky-deep hover:underline"><RefreshCw size={13} strokeWidth={1.75} />Refresh</button>
            </div>
            <p className="mt-1 text-caption text-muted">Sent to Meta whenever a lead changes stage. Meta returns success even when a dataset isn't set up for optimisation, so this log is the real check.</p>
            {events === null ? <PageLoader label="Loading events…" className="min-h-[160px]" /> : events.error ? <AccessNotice reason={events.error} onRetry={loadEvents} compact className="mt-4" /> : (
              <>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {[{ k: "sent", label: "Sent", cls: "text-success" }, { k: "skipped", label: "Skipped", cls: "text-warning" }, { k: "failed", label: "Failed", cls: "text-danger" }].map((s) => (
                    <div key={s.k} className="rounded-md bg-mist p-3 text-center"><p className={`text-h3 font-semibold tabular-nums ${s.cls}`}>{num(events.counts?.[s.k])}</p><p className="mt-0.5 text-caption text-muted">{s.label}</p></div>
                  ))}
                </div>
                {events.skip_reasons?.length > 0 && (
                  <div className="mt-4 rounded-md bg-warning-soft p-3">
                    <p className="mb-1.5 text-caption font-semibold text-warning">Why events were skipped</p>
                    {events.skip_reasons.map((r) => <div key={r.skip_reason} className="flex items-start justify-between gap-2 py-0.5 text-caption"><span className="min-w-0 break-words">{r.skip_reason}</span><span className="shrink-0 font-semibold tabular-nums">{num(r.count)}</span></div>)}
                  </div>
                )}
                {!events.events?.length ? <p className="py-6 text-center text-caption text-muted">No events yet. Change a lead's status to generate one.</p> : (
                  <div className="mt-3 max-h-72 overflow-y-auto">
                    {events.events.map((e) => (
                      <div key={e.id} className="flex items-start gap-2 border-b border-line py-2 last:border-0">
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-caption font-semibold ${EVENT_TONE[e.status] || "bg-warning-soft text-warning"}`}>{e.status}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-caption font-medium">{e.event_name}{e.lead_name ? <span className="font-normal text-muted"> · {e.lead_name}</span> : null}</p>
                          <p className="truncate text-caption text-muted">{e.from_status} → {e.to_status}{e.meta_lead_id ? <span className="ml-1 font-mono">#{String(e.meta_lead_id).slice(-6)}</span> : null}</p>
                          {(e.skip_reason || e.error_message) && <p className="mt-0.5 break-words text-caption text-warning">{e.skip_reason || e.error_message}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          <section className="rounded-lg bg-paper p-5 shadow-soft">
            <h2 className="text-body font-semibold">Send a test event</h2>
            <p className="mt-1 text-caption text-muted">Uses your most recent Meta lead so the lead ID match is exercised, not just the connection. Paste a code from Events Manager → Test Events and Meta will keep it out of your real numbers.</p>
            <div className="mt-3 flex gap-2">
              <TextInput aria-label="Test event code" value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="TEST12345" className="min-w-0 flex-1" />
              <PrimaryButton type="button" onClick={sendTestEvent} loading={sendingTest} loadingLabel="Sending…" className="shrink-0">Send</PrimaryButton>
            </div>
            {!testCode && <p className="mt-2 flex items-center gap-1.5 text-caption text-warning"><TriangleAlert size={13} strokeWidth={1.75} className="shrink-0" />Without a code the event counts as real. Add one to test safely.</p>}
            {testEvent && (
              <div className="mt-3">
                <Notice tone={testEvent.ok ? "success" : "danger"} title={testEvent.ok ? "Meta accepted the event" : "Meta rejected the event"}>
                  {testEvent.message}
                  {testEvent.ok && testEvent.data && <span className="mt-1 block text-caption">Dataset {testEvent.data.dataset_id} · matched on {(testEvent.data.matched_on || []).join(", ") || "nothing"}</span>}
                </Notice>
              </div>
            )}
          </section>

          {stats?.error ? <AccessNotice reason={stats.error} onRetry={loadStats} compact /> : clicks ? (
            <>
              <section className="rounded-lg bg-paper p-5 shadow-soft">
                <h2 className="text-body font-semibold">Ad click tracking</h2>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {[{ val: clicks.total_clicks, label: "Total clicks tracked" }, { val: clicks.unique_phones, label: "Unique phone numbers" }, { val: clicks.matched_count, label: "Matched to leads" }, { val: clicks.unique_campaigns, label: "Campaigns tracked" }].map((s) => (
                    <div key={s.label} className="rounded-md bg-mist p-3 text-center"><p className="text-h3 font-semibold tabular-nums">{num(s.val)}</p><p className="mt-0.5 text-caption text-muted">{s.label}</p></div>
                  ))}
                </div>
              </section>
              {stats.top_campaigns?.length > 0 && (
                <section className="rounded-lg bg-paper p-5 shadow-soft">
                  <h2 className="text-body font-semibold">Top performing campaigns</h2>
                  <div className="mt-4 flex flex-col gap-2">
                    {stats.top_campaigns.map((c, i) => (
                      <div key={`${c.campaign_name}-${i}`} className="rounded-md border border-line p-3">
                        <p className="truncate text-small font-medium">{c.campaign_name || "Unnamed campaign"}</p>
                        <div className="mt-1.5 flex flex-wrap gap-2 text-caption font-semibold"><span className="rounded-full bg-sky-soft px-2 py-0.5 text-sky-deep">{num(c.leads_count)} leads</span><span className="rounded-full bg-success-soft px-2 py-0.5 text-success">{num(c.qualified_count)} qualified</span></div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {stats.recent_clicks?.length > 0 && (
                <section className="rounded-lg bg-paper p-5 shadow-soft">
                  <h2 className="text-body font-semibold">Recent ad clicks</h2>
                  <div className="mt-4 flex flex-col gap-2">
                    {stats.recent_clicks.slice(0, 5).map((c, i) => (
                      <div key={`${c.phone_number}-${i}`} className="flex items-center justify-between gap-3 rounded-md border border-line p-3">
                        <div className="min-w-0"><p className="truncate text-small font-medium">{c.phone_number}</p><p className="truncate text-caption text-muted">{c.campaign_name}</p></div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-caption font-semibold ${c.matched ? "bg-success-soft text-success" : "bg-warning-soft text-warning"}`}>{c.matched ? "Matched" : "Pending"}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <section className="flex flex-col items-center gap-3 rounded-lg bg-paper px-6 py-8 text-center shadow-soft">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-mist text-muted"><BarChart3 size={22} strokeWidth={1.75} /></span>
              <p className="text-body font-semibold">No data yet</p>
              <p className="text-small text-muted">Set up the integration to start tracking ad performance.</p>
            </section>
          )}

          <section className="rounded-lg bg-paper p-5 shadow-soft">
            <h2 className="text-body font-semibold">Setup guide</h2>
            <ol className="mt-4 flex flex-col gap-4">
              {STEPS.map((s, i) => (
                <li key={s.title} className="flex items-start gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sun text-caption font-semibold">{i + 1}</span>
                  <div className="pt-0.5"><p className="text-small font-semibold">{s.title}</p><p className="mt-0.5 text-caption text-muted">{s.desc}</p></div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this integration?"
          body="Ad tracking will stop working."
          confirmLabel="Delete integration"
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </main>
  );
}
